import type { ConfiguratorV2ConfigurationDTO, ConfiguratorV2CsticDTO } from "./configuratorV2Api.types";
import type { ConfiguratorConfiguration, ConfiguratorCstic, ConfiguratorInstance } from "../contract/configuration";

const MAIN_KMAT_TYPE = "Main";

const normalizeFingerprint = (fingerprint: string | null): string | null => fingerprint?.trim() || null;

const csticKey = (instanceId: string, csticName: string) => `${instanceId}@${csticName}`;

const adaptCstic = (instanceId: string, dto: ConfiguratorV2CsticDTO): ConfiguratorCstic => ({
  key: csticKey(instanceId, dto.name),
  name: dto.name,
  labelKey: dto.label,
  label: dto.label,
  description: dto.description,
  summaryLabel: dto.summaryLabel,
  info: dto.info,
  imageUrl: dto.imageUrl,
  value: dto.value ?? null,
  values: dto.values.map((value) => ({
    value: value.value,
    labelKey: value.label,
    label: value.label,
    description: value.description,
    info: value.info,
    imageUrl: value.imageUrl,
    selectable: value.selectable,
    standard: value.standard,
  })),
  visible: dto.visible,
  readOnly: dto.readOnly,
  unit: dto.unit,
});

const adaptInstances = (dto: ConfiguratorV2ConfigurationDTO): ConfiguratorInstance[] => {
  const indexesByType = new Map<string, number>();

  return dto.instances.map((instance) => {
    const kmatIndex = indexesByType.get(instance.type) ?? 0;
    indexesByType.set(instance.type, kmatIndex + 1);

    return {
      id: instance.id,
      parentId: instance.parentId ?? undefined,
      kind: instance.type === MAIN_KMAT_TYPE ? "main" : "sub",
      kmatType: instance.type,
      kmatIndex,
      cstics: Object.fromEntries(instance.cstics.map((cstic) => [cstic.name, adaptCstic(instance.id, cstic)])),
    };
  });
};

export const adaptConfiguratorConfiguration = (dto: ConfiguratorV2ConfigurationDTO): ConfiguratorConfiguration => ({
  configId: dto.configId,
  productCode: dto.productCode,
  priceFingerprint: normalizeFingerprint(dto.priceFingerprint),
  availabilityFingerprint: normalizeFingerprint(dto.availabilityFingerprint),
  productShortDesignation: dto.productShortDesignation,
  description: dto.description,
  attachment: dto.attachment
    ? {
        fileName: dto.attachment.fileName,
        url: dto.attachment.url,
        fileSizeBytes: dto.attachment.fileSizeBytes,
        uploadedAt: dto.attachment.uploadedAt,
      }
    : undefined,
  helpUrl: null,
  summaryImageUrls: dto.summaryImageUrls,
  instances: adaptInstances(dto),
  conflicts: dto.conflicts.map((conflict) => ({
    id: conflict.id,
    cstics: conflict.cstics.map(({ instanceId, csticName }) => ({
      key: csticKey(instanceId, csticName),
      name: csticName,
    })),
  })),
  currentStepNumber: dto.currentStepNumber,
  highestVisitedStepNumber: dto.highestVisitedStepNumber,
});
